// public/js/features/files/photo-gallery.js
// The asset photo gallery that sits at the top of Basic Information: pictures
// only, no captions, no metadata — the Photos section further down is the place
// for that. Here the job is simply "show me this machine".
//
// It renders NOTHING when the asset has no photos, and the add button lives
// inside it, so the first photo is always added from the Photos section below.
// That avoids an empty frame with an upload prompt competing with the map and
// the fields for the most valuable space on the page.
import { getFileUrl, listAssetFiles } from './api.js';
import { can, getCurrentUser } from '../../modules/rbac.js';
import { openUploader } from './uploader.js';
import { escapeHtml } from '../../utils/escape.js';

let cssInjected = false;
function injectCss() {
  if (cssInjected) return;
  cssInjected = true;
  const el = document.createElement('style');
  el.textContent = `
  .pg-wrap { position: relative; }
  .pg-main { position: relative; width: 100%; aspect-ratio: 4 / 3; border-radius: .5rem;
             overflow: hidden; background: var(--bs-tertiary-bg, #f1f3f5); cursor: zoom-in; }
  .pg-main img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .pg-nav { position: absolute; top: 50%; transform: translateY(-50%); z-index: 2;
            border: 0; width: 34px; height: 34px; border-radius: 50%; cursor: pointer;
            background: rgba(17,20,24,.55); color: #fff; display: flex;
            align-items: center; justify-content: center; opacity: 0; transition: opacity .15s; }
  .pg-wrap:hover .pg-nav, .pg-nav:focus-visible { opacity: 1; }
  .pg-nav:hover { background: rgba(17,20,24,.8); }
  .pg-nav[disabled] { opacity: 0 !important; pointer-events: none; }
  .pg-prev { left: .5rem; } .pg-next { right: .5rem; }
  .pg-count { position: absolute; bottom: .5rem; right: .5rem; z-index: 2;
              background: rgba(17,20,24,.6); color: #fff; border-radius: 999px;
              padding: .1rem .5rem; font-size: .72rem; letter-spacing: .02em; }
  .pg-add { position: absolute; top: .5rem; right: .5rem; z-index: 3; border: 0;
            width: 30px; height: 30px; border-radius: 50%; cursor: pointer;
            background: rgba(17,20,24,.6); color: #fff; font-size: .75rem;
            display: flex; align-items: center; justify-content: center; gap: 1px; }
  .pg-add:hover { background: rgba(17,20,24,.85); }
  .pg-strip { display: flex; gap: .35rem; margin-top: .35rem; overflow-x: auto;
              scrollbar-width: thin; padding-bottom: 2px; }
  .pg-strip button { flex: 0 0 auto; width: 46px; height: 46px; padding: 0; border-radius: .35rem;
                     overflow: hidden; border: 2px solid transparent; background: var(--bs-tertiary-bg, #f1f3f5); cursor: pointer; }
  .pg-strip button.active { border-color: var(--bs-primary, #0d6efd); }
  .pg-strip img { width: 100%; height: 100%; object-fit: cover; display: block; }
  /* Fullscreen viewer */
  .pg-modal .modal-content { background: rgba(12,14,17,.97); border: 0; }
  .pg-modal .modal-body { display: flex; align-items: center; justify-content: center; padding: 0; }
  .pg-modal img { max-width: 100%; max-height: calc(100vh - 7rem); object-fit: contain; }
  .pg-modal .pg-nav { opacity: 1; width: 46px; height: 46px; }
  .pg-modal .pg-close { position: absolute; top: 1rem; right: 1rem; z-index: 5; }
  .pg-modal .pg-count { bottom: 1rem; left: 50%; right: auto; transform: translateX(-50%); }
  `;
  document.head.appendChild(el);
}

/**
 * Render the gallery into `container`.
 * Resolves to true when photos were shown, false when there are none — the
 * caller uses that to decide its column layout.
 */
export async function renderPhotoGallery(container, assetUUID, { onChanged } = {}) {
  if (!container) return false;
  injectCss();

  let photos = [];
  try {
    photos = (await listAssetFiles(assetUUID, { kind: 'photo' })) || [];
  } catch (e) {
    console.warn('[gallery] could not load photos:', e?.message);
    container.innerHTML = '';
    return false;
  }
  // Front image first, then newest: the gallery opens on the picture that
  // represents this asset everywhere else in the app.
  photos.sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0)
    || String(b.uploadedAt || '').localeCompare(String(a.uploadedAt || '')));

  if (!photos.length) { container.innerHTML = ''; return false; }

  const canManage = can(getCurrentUser()?.groups || [], 'manageAssetFiles');
  let index = 0;
  const urls = new Map();   // fileId -> { web, thumb }

  const urlFor = async (file, variant) => {
    const cached = urls.get(file.fileId) || {};
    if (cached[variant]) return cached[variant];
    const raw = await getFileUrl(file.fileId, variant);
    const url = (typeof raw === 'string' ? raw : raw?.url) || null;
    urls.set(file.fileId, { ...cached, [variant]: url });
    return url;
  };

  container.innerHTML = `
    <div class="pg-wrap">
      <div class="pg-main" data-role="open" title="Click to enlarge">
        <img alt="${escapeHtml(`Photo of this asset`)}">
        <button type="button" class="pg-nav pg-prev" data-step="-1" aria-label="Previous photo"><i class="fa-solid fa-chevron-left"></i></button>
        <button type="button" class="pg-nav pg-next" data-step="1" aria-label="Next photo"><i class="fa-solid fa-chevron-right"></i></button>
        ${photos.length > 1 ? '<span class="pg-count"></span>' : ''}
        ${canManage ? '<button type="button" class="pg-add" data-role="add" title="Add photos"><i class="fa-solid fa-camera"></i><i class="fa-solid fa-plus" style="font-size:.55em"></i></button>' : ''}
      </div>
      ${photos.length > 1 ? `<div class="pg-strip">${photos.map((p, i) =>
        `<button type="button" data-i="${i}" class="${i === 0 ? 'active' : ''}"><img alt=""></button>`).join('')}</div>` : ''}
    </div>`;

  const mainImg = container.querySelector('.pg-main img');
  const count = container.querySelector('.pg-count');
  const strip = [...container.querySelectorAll('.pg-strip button')];

  async function show(i) {
    index = (i + photos.length) % photos.length;
    const url = await urlFor(photos[index], 'web');
    if (url) mainImg.src = url;
    if (count) count.textContent = `${index + 1} / ${photos.length}`;
    strip.forEach((b, n) => b.classList.toggle('active', n === index));
    const single = photos.length < 2;
    container.querySelectorAll('.pg-main .pg-nav').forEach(b => { b.disabled = single; });
  }

  container.querySelectorAll('.pg-main .pg-nav').forEach(btn => btn.addEventListener('click', (ev) => {
    ev.stopPropagation();                       // do not also open the viewer
    show(index + Number(btn.dataset.step));
  }));
  strip.forEach(btn => btn.addEventListener('click', () => show(Number(btn.dataset.i))));
  container.querySelector('[data-role="open"]')?.addEventListener('click', () => openViewer(photos, index, urlFor));
  container.querySelector('[data-role="add"]')?.addEventListener('click', (ev) => {
    ev.stopPropagation();
    openUploader({ assetUUID, kind: 'photo', onDone: () => onChanged?.() });
  });

  await show(0);
  // Thumbnails after the main image: the big one is what the user waits for.
  photos.forEach(async (p, i) => {
    if (!strip[i]) return;
    const u = await urlFor(p, 'thumb').catch(() => null);
    if (u) strip[i].querySelector('img').src = u;
  });
  return true;
}

/** Fullscreen viewer with the same click-through, plus arrow keys. */
function openViewer(photos, startIndex, urlFor) {
  injectCss();
  let i = startIndex;
  const el = document.createElement('div');
  el.className = 'modal fade pg-modal';
  el.innerHTML = `
    <div class="modal-dialog modal-fullscreen">
      <div class="modal-content">
        <button type="button" class="btn-close btn-close-white pg-close" data-bs-dismiss="modal" aria-label="Close"></button>
        <div class="modal-body">
          <button type="button" class="pg-nav pg-prev" data-step="-1" aria-label="Previous photo"><i class="fa-solid fa-chevron-left"></i></button>
          <img alt="">
          <button type="button" class="pg-nav pg-next" data-step="1" aria-label="Next photo"><i class="fa-solid fa-chevron-right"></i></button>
          ${photos.length > 1 ? '<span class="pg-count"></span>' : ''}
        </div>
      </div>
    </div>`;
  document.body.appendChild(el);

  const img = el.querySelector('img');
  const count = el.querySelector('.pg-count');
  const single = photos.length < 2;
  el.querySelectorAll('.pg-nav').forEach(b => { b.disabled = single; });

  async function show(n) {
    i = (n + photos.length) % photos.length;
    const url = await urlFor(photos[i], 'web');
    if (url) img.src = url;
    if (count) count.textContent = `${i + 1} / ${photos.length}`;
  }
  el.querySelectorAll('.pg-nav').forEach(btn =>
    btn.addEventListener('click', () => show(i + Number(btn.dataset.step))));

  const onKey = (ev) => {
    if (ev.key === 'ArrowRight') show(i + 1);
    else if (ev.key === 'ArrowLeft') show(i - 1);
  };
  document.addEventListener('keydown', onKey);

  const modal = new bootstrap.Modal(el);
  el.addEventListener('hidden.bs.modal', () => {
    document.removeEventListener('keydown', onKey);
    el.remove();
  });
  show(i);
  modal.show();
}
